/**
 * The bidder in a dead spot.
 *
 * Same IndexedDB as the crew ('kam-offline'): the day's visits are cached
 * so "what's my next stop" still answers with no signal, and anything the
 * bidder does in a yard — an on-site intake with its photos, a skip, an
 * "on the way" text — is queued and sent, in order, when signal returns.
 * Queued rows carry kind:'bidder' so the crew's flush leaves them alone.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../services/api';

const DB_NAME = 'kam-offline';
const VERSION = 2;
const ACTIONS = 'pending-actions';
const CACHE = 'day-cache';

export type BidderAction = { key?: number; kind: 'bidder'; method: 'POST' | 'PUT'; url: string; body: any; label: string; queuedAt: number };

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('pending-photos')) db.createObjectStore('pending-photos', { autoIncrement: true });
      if (!db.objectStoreNames.contains(ACTIONS)) db.createObjectStore(ACTIONS, { autoIncrement: true });
      if (!db.objectStoreNames.contains(CACHE)) db.createObjectStore(CACHE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
function idb<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDB().then(db => new Promise<T>((resolve, reject) => {
    const req = fn(db.transaction(store, mode).objectStore(store));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
  }));
}

export async function cacheBidder(key: string, value: any): Promise<void> {
  try { await idb(CACHE, 'readwrite', s => s.put({ value, at: Date.now() }, `bidder:${key}`)); } catch { /* storage unavailable */ }
}
export async function readBidderCache<T>(key: string): Promise<{ value: T; at: number } | null> {
  try { return (await idb<any>(CACHE, 'readonly', s => s.get(`bidder:${key}`))) || null; } catch { return null; }
}

async function allBidderActions(): Promise<BidderAction[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const out: BidderAction[] = [];
    const req = db.transaction(ACTIONS, 'readonly').objectStore(ACTIONS).openCursor();
    req.onsuccess = e => {
      const c = (e.target as IDBRequest<IDBCursorWithValue | null>).result;
      if (c) { if (c.value?.kind === 'bidder') out.push({ key: c.key as number, ...c.value }); c.continue(); } else resolve(out);
    };
    req.onerror = () => reject(req.error);
  });
}

// Told to every mounted useBidderOffline when something goes into the queue,
// so the "N things waiting to send" strip shows the moment he taps — not the
// next time the page happens to look.
const QUEUED_EVENT = 'kam-bidder-queued';

/** Put a request in the queue. Returns false when storage is unavailable. */
export async function queueBidderAction(a: Omit<BidderAction, 'key' | 'kind' | 'queuedAt'>): Promise<boolean> {
  try {
    await idb(ACTIONS, 'readwrite', s => s.add({ ...a, kind: 'bidder', queuedAt: Date.now() }));
    try { window.dispatchEvent(new Event(QUEUED_EVENT)); } catch { /* no window */ }
    return true;
  } catch { return false; }
}

/** A failed request that never reached the server (no signal), as opposed to one the server refused. */
export function isNetworkFailure(err: any): boolean {
  return !err?.response && (err?.code === 'ERR_NETWORK' || /network|failed to fetch/i.test(String(err?.message || '')) || !navigator.onLine);
}

/**
 * The queue, on any page that has the visit cards — the bidder's Today tab and
 * the owner's Field page.
 *
 * Owner, Oct 5 2026: "I want the optimize and re route button to actually
 * work"; "you have time to fully fix it — make this a fully functioning
 * feature". On Field a "Here" tapped in a dead spot said "goes out when you
 * are back online" and never went: this hook, the only thing that sends the
 * queue, was mounted on the Today tab alone. It is mounted on Field now, with
 * the same "waiting to send" strip.
 *
 * `onSynced` is kept in a ref: callers pass a new function on every render,
 * and with it in the effect's dependencies the effect ran again after every
 * render — reading the queue, setting state, rendering, reading the queue —
 * for as long as the page was open.
 */
export function useBidderOffline(onSynced?: () => void) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState<BidderAction[]>([]);
  const flushing = useRef(false);
  const synced = useRef(onSynced);
  synced.current = onSynced;
  const refresh = useCallback(async () => {
    let next: BidderAction[] = [];
    try { next = await allBidderActions(); } catch { next = []; }
    // The same queue is the same state — no new render for an unchanged list.
    setPending(prev => (prev.length === next.length && prev.every((p, i) => p.key === next[i].key) ? prev : next));
  }, []);

  const flush = useCallback(async () => {
    if (flushing.current || !navigator.onLine) return;
    flushing.current = true;
    try {
      const items = await allBidderActions();
      const token = localStorage.getItem('token');
      let sent = 0;
      for (const item of items) {
        try {
          const res = await fetch(`${API_BASE_URL}${item.url}`, {
            method: item.method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ ...item.body, client_ts: item.queuedAt }),
          });
          // 401 / 403: the login expired or was signed out — keep the work for after re-login.
          if (res.status === 401 || res.status === 403) break;
          // A real refusal (400/404/409/422) is final and comes off the queue with a note; no answer = still no signal.
          if (res.ok || [400, 404, 409, 410, 422].includes(res.status)) {
            await idb(ACTIONS, 'readwrite', s => s.delete(item.key as number));
            if (res.ok) sent++;
            else { let msg = ''; try { msg = (await res.json())?.message || ''; } catch { /* no body */ } toast.error(`${item.label}: ${msg || `refused (${res.status})`}`); }
          } else break;
        } catch { break; }
      }
      if (sent) { toast.success(`${sent} thing${sent === 1 ? '' : 's'} from the yard sent now you're back online`); synced.current?.(); }
    } finally { flushing.current = false; await refresh(); }
  }, [refresh]);

  // When to try sending. The phone saying "online" is not enough on its own: in
  // a dead spot it often still says it has signal (the request simply fails),
  // and then no "online" event ever comes. So the queue is also tried when he
  // comes back to the page (it shows again, or gets focus) and once a minute
  // while something is waiting.
  const waiting = useRef(0);
  waiting.current = pending.length;
  useEffect(() => {
    const on = () => { setIsOnline(true); flush(); };
    const off = () => setIsOnline(false);
    const queued = () => { refresh(); };
    const back = () => { if (document.visibilityState === 'visible') { setIsOnline(navigator.onLine); flush(); } };
    window.addEventListener('online', on); window.addEventListener('offline', off); window.addEventListener(QUEUED_EVENT, queued);
    window.addEventListener('focus', back); document.addEventListener('visibilitychange', back);
    const again = setInterval(() => { if (waiting.current) flush(); }, 60_000);
    refresh(); flush();
    return () => {
      window.removeEventListener('online', on); window.removeEventListener('offline', off); window.removeEventListener(QUEUED_EVENT, queued);
      window.removeEventListener('focus', back); document.removeEventListener('visibilitychange', back);
      clearInterval(again);
    };
  }, [flush, refresh]);

  return { isOnline, pending, flush, refresh };
}
